import React, { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
import { 
  InformationCircleIcon,
  XMarkIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  CheckCircleIcon,
  PlusIcon,
  PencilIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';
import { Button } from './button';
import { useBranding } from '../../contexts/BrandingContext';
import { Card } from './card';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, ResponsiveContainer } from 'recharts';

// Contextual Help Tooltip Component
export function HelpTooltip({ 
  content, 
  position = 'top', 
  children, 
  className = '',
  showOnHover = true 
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [hasSeen, setHasSeen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(position);
  const triggerRef = useRef(null);

  useEffect(() => {
    const tooltipId = content?.slice(0, 20); // Simple ID based on content
    const seen = localStorage.getItem(`help-tooltip-${tooltipId}`);
    setHasSeen(!!seen);
  }, [content]);

  const calculatePosition = () => {
    if (!triggerRef.current || !isVisible) return position;
    
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const tooltipWidth = 256; // w-64 = 256px
    const margin = 16; // 1rem margin from viewport edge
    
    // Check if tooltip would overflow on the right
    if (rect.right + tooltipWidth + margin > viewportWidth) {
      // If on the right side of screen, position to the left
      if (rect.left > viewportWidth / 2) {
        return 'left';
      }
      // If tooltip would still overflow to the left, use top
      if (rect.left - tooltipWidth - margin < 0) {
        return 'top';
      }
    }
    
    // Check if tooltip would overflow on the left
    if (rect.left - tooltipWidth - margin < 0) {
      return 'right';
    }
    
    return position;
  };

  useEffect(() => {
    if (isVisible) {
      const newPosition = calculatePosition();
      setTooltipPosition(newPosition);
    }
  }, [isVisible, position]);

  const handleClick = () => {
    if (!hasSeen) {
      setIsVisible(true);
      const tooltipId = content?.slice(0, 20);
      localStorage.setItem(`help-tooltip-${tooltipId}`, 'true');
      setHasSeen(true);
    }
  };

  const positionClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2 max-w-[calc(100vw-2rem)]',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2 max-w-[calc(100vw-2rem)]',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-2 max-w-[calc(100vw-2rem)]',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-2 max-w-[calc(100vw-2rem)]'
  };

  return (
    <div className="relative inline-block">
      <div 
        ref={triggerRef}
        className={cn("cursor-help", className)}
        onMouseEnter={showOnHover ? () => setIsVisible(true) : undefined}
        onMouseLeave={showOnHover ? () => setIsVisible(false) : undefined}
        onClick={handleClick}
      >
        {children}
      </div>
      
      {isVisible && (
        <div className={cn(
          "absolute z-50 w-64 max-w-[calc(100vw-2rem)] p-3 bg-popover border rounded-lg shadow-lg",
          positionClasses[tooltipPosition]
        )}>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-2">
                <InformationCircleIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <span className="text-sm font-medium">Quick Tip</span>
              </div>
              <p className="text-sm text-muted-foreground break-words">{content}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 ml-2 flex-shrink-0"
              onClick={() => setIsVisible(false)}
            >
              <XMarkIcon className="h-3 w-3" />
            </Button>
          </div>
          <div className="absolute w-2 h-2 bg-popover border border-border transform rotate-45 -translate-x-1/2 -translate-y-1/2" 
               style={{
                 [position === 'top' ? 'top' : 'bottom']: '-4px',
                 left: '50%',
                 borderRight: position === 'top' ? 'none' : undefined,
                 borderLeft: position === 'top' ? 'none' : undefined,
                 borderTop: position === 'bottom' ? 'none' : undefined,
                 borderBottom: position === 'bottom' ? 'none' : undefined
               }} />
        </div>
      )}
    </div>
  );
}

// Guided Tour Component
export function GuidedTour({ 
  steps = [], 
  isActive = false, 
  onComplete, 
  onSkip,
  className = '' 
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(isActive);

  const currentStepData = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    onComplete?.();
  };

  const handleSkip = () => {
    setIsVisible(false);
    onSkip?.();
  };

  if (!isVisible || !currentStepData) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
      <Card className={cn("w-96 p-6 relative", className)}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-white">
                {currentStep + 1}
              </span>
            </div>
            <span className="text-sm text-muted-foreground">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          <Button variant="ghost" size="icon" onClick={handleSkip}>
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">{currentStepData.title}</h3>
          <p className="text-muted-foreground">{currentStepData.description}</p>
          {currentStepData.action && (
            <div className="mt-3 p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-1">Try this:</p>
              <p className="text-sm text-muted-foreground">{currentStepData.action}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "w-2 h-2 rounded-full",
                  index === currentStep ? "bg-primary" : "bg-muted"
                )}
              />
            ))}
          </div>
          
          <div className="flex space-x-2">
            {currentStep > 0 && (
              <Button variant="outline" size="sm" onClick={handlePrevious}>
                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                Previous
              </Button>
            )}
            <Button size="sm" onClick={handleNext}>
              {currentStep === steps.length - 1 ? (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-1" />
                  Complete
                </>
              ) : (
                <>
                  Next
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Smart Mini Chart Component - Chooses chart type based on data characteristics and metric type
const MiniTrendChart = ({ data, color, changeType, metricType = 'count', chartType: explicitChartType, branding }) => {
  if (!data || data.length < 2) return null;

  // Determine chart type based on data characteristics and metric nature
  const getChartType = () => {
    // If explicitly set, use that
    if (explicitChartType) return explicitChartType;
    
    // For time series data (dates)
    if (data[0]?.date) {
      // Use line chart for discrete/count metrics (cleaner look)
      if (metricType === 'count' || metricType === 'discrete') {
        // For small datasets or when we want emphasis on individual points
        if (data.length <= 14) {
          return 'line';
        }
        // For continuous time series, use area to show volume
        return 'area';
      }
      // Use area chart for cumulative/aggregated metrics
      if (metricType === 'cumulative' || metricType === 'aggregate') {
        return 'area';
      }
      // Use line chart for rates/percentages (cleaner)
      if (metricType === 'rate' || metricType === 'percentage') {
        return 'line';
      }
      // Default to area for time series
      return 'area';
    }
    
    // For categorical data or discrete values, use bar chart
    if (data.length <= 7) {
      return 'bar';
    }
    
    // Default to area chart for continuous data
    return 'area';
  };

  const chartType = getChartType();

  // Use branding colors with fallbacks, ensuring dark mode compatibility
  const primaryColor = branding?.primaryColor || '#3b82f6';
  const secondaryColor = branding?.secondaryColor || '#10b981';
  
  // Convert hex to RGB for opacity calculations
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const getChartColor = () => {
    if (changeType === 'positive') {
      return secondaryColor; // Use branding secondary (usually green)
    } else if (changeType === 'negative') {
      return '#ef4444'; // Keep red for negative (universal)
    } else {
      return primaryColor; // Use branding primary for neutral
    }
  };

  const chartColor = getChartColor();
  
  // Calculate light version with opacity for gradients
  const rgb = hexToRgb(chartColor);
  const chartColorLight = rgb 
    ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`
    : 'rgba(99, 102, 241, 0.3)';

  // Normalize data for better visualization
  const max = Math.max(...data.map(d => d.value));
  const min = Math.min(...data.map(d => d.value));
  const range = max - min || 1;

  const normalizedData = data.map(d => ({
    ...d,
    normalized: ((d.value - min) / range) * 100
  }));

  // Generate unique gradient ID to avoid conflicts
  const gradientId = `gradient-${color}-${changeType}-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className="h-14 w-full">
      <ResponsiveContainer width="100%" height="100%">
        {chartType === 'area' ? (
          <AreaChart data={normalizedData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor} stopOpacity={0.4} />
                <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="normalized"
              stroke={chartColor}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        ) : chartType === 'line' ? (
          <LineChart data={normalizedData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <Line
              type="monotone"
              dataKey="normalized"
              stroke={chartColor}
              strokeWidth={2.5}
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          </LineChart>
        ) : (
          <BarChart data={normalizedData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <Bar
              dataKey="normalized"
              fill={chartColor}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

// Smart Dashboard Card Component - Enhanced with modern UI patterns
export function SmartDashboardCard({ 
  title, 
  value, 
  change, 
  changeType = 'neutral',
  icon: Icon,
  action,
  helpText,
  className = '',
  trend,
  sparkline,
  trendData, // Array of { date, value } for past month
  color = 'primary',
  metricType = 'count', // 'count', 'cumulative', 'rate', 'discrete', 'aggregate'
  chartType, // Explicit chart type: 'area', 'line', 'bar'
  changeValue, // Numeric change value (e.g., 23, -36) for better trend display
  changeLabel // Label for change period (e.g., "7 Days", "Last Month")
}) {
  const { branding } = useBranding();
  const primaryColor = branding?.primaryColor || '#3b82f6';
  
  const changeColors = {
    positive: 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950/50',
    negative: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-950/50',
    neutral: 'text-muted-foreground bg-muted'
  };

  // Icon colors with branding support and dark mode
  const getIconColor = (colorName) => {
    // Use branding colors for primary
    if (colorName === 'primary') {
      return {
        background: `bg-[${primaryColor}]/10`,
        text: `text-[${primaryColor}]`
      };
    }
    
    // Semantic colors with dark mode support
    const colorMap = {
      blue: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400' },
      green: { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400' },
      purple: { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400' },
      orange: { bg: 'bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400' },
      red: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400' },
      indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-600 dark:text-indigo-400' },
      cyan: { bg: 'bg-cyan-500/10', text: 'text-cyan-600 dark:text-cyan-400' },
      amber: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' }
    };
    
    return colorMap[colorName] || colorMap.blue;
  };

  const iconColorStyle = getIconColor(color);

  // Simple sparkline renderer (legacy support)
  const renderSparkline = () => {
    if (!sparkline || sparkline.length < 2) return null;
    
    const max = Math.max(...sparkline);
    const min = Math.min(...sparkline);
    const range = max - min || 1;
    const width = 60;
    const height = 20;
    const points = sparkline.map((val, idx) => {
      const x = (idx / (sparkline.length - 1)) * width;
      const y = height - ((val - min) / range) * height;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg 
        width={width} 
        height={height} 
        className="absolute bottom-3 right-3 opacity-60"
        viewBox={`0 0 ${width} ${height}`}
      >
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={changeType === 'positive' ? 'text-emerald-500' : changeType === 'negative' ? 'text-red-500' : 'text-muted-foreground'}
        />
      </svg>
    );
  };

  // Calculate trend percentage from change text if available
  const getTrendPercentage = () => {
    if (trend !== undefined && trend !== null) return trend;
    if (change) {
      // Try to extract percentage from change text (e.g., "+7 this month" -> 7)
      const match = change.match(/([+-]?\d+)/);
      if (match) {
        const num = parseInt(match[1]);
        return num > 0 ? Math.min(num, 100) : Math.max(num, -100);
      }
    }
    return null;
  };

  const trendPercent = getTrendPercentage();
  const isPositiveTrend = trendPercent !== null && trendPercent > 0;
  const isNegativeTrend = trendPercent !== null && trendPercent < 0;

  return (
    <Card 
      className={cn(
        "group relative transition-all duration-300 cursor-pointer",
        "hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5",
        "border-border/50 hover:border-border",
        "min-w-0 w-full overflow-hidden bg-card",
        action && "cursor-pointer",
        className
      )}
      onClick={action}
    >
      <div className="p-4 min-w-0 relative z-10">
        {/* Compact layout: Icon top-left, Value center-left, Trend top-right, Chart bottom */}
        <div className="space-y-3">
          {/* Top row: Icon + Trend */}
          <div className="flex items-start justify-between">
            {/* Icon in square/circle */}
            {Icon && (
              <div 
                className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0 transition-transform duration-300 group-hover:scale-105",
                  iconColorStyle.bg
                )}
                style={color === 'primary' ? {
                  backgroundColor: `${primaryColor}15`,
                  color: primaryColor
                } : undefined}
              >
                <Icon className={cn("h-4 w-4", color !== 'primary' && iconColorStyle.text)} />
              </div>
            )}
            
            {/* Trend indicator - top right - Enhanced with colored numbers like reference image */}
            {(trendPercent !== null || changeValue !== undefined) && (
              <div className="flex flex-col items-end gap-0.5">
                <div className={cn(
                  "flex items-center gap-1 text-xs font-bold",
                  isPositiveTrend ? "text-emerald-600 dark:text-emerald-400" : 
                  isNegativeTrend ? "text-red-600 dark:text-red-400" : 
                  "text-muted-foreground"
                )}>
                  {isPositiveTrend && <ArrowTrendingUpIcon className="h-3.5 w-3.5" />}
                  {isNegativeTrend && <ArrowTrendingDownIcon className="h-3.5 w-3.5" />}
                  <span>
                    {changeValue !== undefined 
                      ? (isPositiveTrend ? '+' : '') + changeValue
                      : `${Math.abs(trendPercent)}%`
                    }
                  </span>
                </div>
                {changeLabel && (
                  <div className="text-[10px] text-muted-foreground font-medium">
                    {changeLabel}
                  </div>
                )}
              </div>
            )}
            
            {/* Help icon - if no trend */}
            {!trendPercent && helpText && (
              <HelpTooltip content={helpText}>
                <button 
                  className="flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <InformationCircleIcon className="h-3.5 w-3.5" />
                </button>
              </HelpTooltip>
            )}
          </div>
          
          {/* Value */}
          <div>
            <div className="text-2xl font-bold tracking-tight text-foreground tabular-nums">{value}</div>
            <h3 className="text-xs font-medium text-muted-foreground mt-0.5">{title}</h3>
          </div>
          
          {/* Mini Trend Chart - full width at bottom */}
          {trendData && trendData.length > 0 && (
            <div className="mt-1.5 -mx-4 -mb-4">
              <MiniTrendChart 
                data={trendData} 
                color={color} 
                changeType={changeType}
                metricType={metricType}
                chartType={chartType}
                branding={branding}
              />
            </div>
          )}
          
          {/* Change badge - below title if no chart */}
          {change && !trendData && (
            <div className={cn(
              "inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium transition-colors mt-1.5",
              changeColors[changeType]
            )}>
              <span>{change}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Enhanced hover effect overlay with gradient */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none" 
      />
      
      {/* Subtle border glow on hover */}
      <div 
        className="absolute inset-0 rounded-lg border border-primary/0 group-hover:border-primary/20 transition-colors duration-300 pointer-events-none"
      />
    </Card>
  );
}

// Quick Action Button Component
export function QuickActionButton({ 
  icon: Icon, 
  label, 
  onClick, 
  variant = 'default',
  className = '' 
}) {
  return (
    <Button
      variant={variant}
      className={cn("flex items-center space-x-2", className)}
      onClick={onClick}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </Button>
  );
}

// Floating Action Menu Component using MUI Speed Dial
export function FloatingActionMenu({ 
  actions = [], 
  isOpen = false, 
  onToggle,
  className = '',
  direction = 'up',
  ariaLabel = 'Speed dial'
}) {
  const { branding } = useBranding();
  
  const handleOpen = () => {
    if (!isOpen) {
      onToggle();
    }
  };
  
  const handleClose = () => {
    if (isOpen) {
      onToggle();
    }
  };

  return (
    <div className={cn("fixed bottom-6 right-6 z-40", className)}>
      <SpeedDial
        ariaLabel={ariaLabel}
        sx={{ position: 'absolute', bottom: 16, right: 16 }}
        icon={<SpeedDialIcon />}
        onClose={handleClose}
        onOpen={handleOpen}
        open={isOpen}
        direction={direction}
      >
        {actions.map((action, index) => (
          <SpeedDialAction
            key={index}
            icon={<action.icon className="h-5 w-5" />}
            onClick={() => {
              action.onClick();
              handleClose();
            }}
            slotProps={{
              tooltip: {
                title: action.label,
              },
            }}
          />
        ))}
      </SpeedDial>
    </div>
  );
}
